import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Maximize2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import LegalContent from '@/components/layout/LegalContent';

interface LegalSheetProps {
  open: boolean;
  title: string;
  /** Raw markdown source of the legal document. */
  content: string;
  /** Route of the full-screen version of the document. */
  fullPageHref: string;
  onClose: () => void;
}

/**
 * Slide-in panel from the right for legal documents (Terms of Service,
 * Privacy Policy). Closes on backdrop click or Escape; locks body scroll
 * while open. Stays mounted so the slide transition plays both ways.
 */
const LegalSheet = ({
  open,
  title,
  content,
  fullPageHref,
  onClose,
}: LegalSheetProps) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    bodyRef.current?.scrollTo({ top: 0 });
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  return (
    <div
      className={cn('fixed inset-0 z-50', !open && 'pointer-events-none')}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        className={cn(
          'absolute inset-0 bg-stone-900/50 transition-opacity duration-300',
          open ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-sheet-title"
        className={cn(
          'absolute inset-y-0 right-0 flex w-full max-w-xl flex-col bg-white text-left shadow-2xl transition-transform duration-300 ease-in-out',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4">
          <h2
            id="legal-sheet-title"
            className="font-serif text-xl text-stone-800"
          >
            {title}
          </h2>
          <div className="flex items-center gap-1">
            <Link
              to={fullPageHref}
              aria-label={`Open ${title} as a full page`}
              title="Open as full page"
              tabIndex={open ? 0 : -1}
              className="rounded-full p-2 text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-800"
            >
              <Maximize2 className="h-4 w-4" />
            </Link>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-full p-2 text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div ref={bodyRef} className="flex-1 overflow-y-auto px-6 py-6">
          <LegalContent markdown={content} />
        </div>
      </div>
    </div>
  );
};

export default LegalSheet;
